import React from "react";
import { Helmet } from "react-helmet-async";

const BASE_URL = "https://prestiqueresume.com";

/**
 * Centralised SEO component. Renders <title>, meta description, canonical,
 * Open Graph, Twitter, and an optional JSON-LD schema graph.
 */
export const Seo = ({
  title,
  description,
  path = "/",
  image = "/brand/logo.png",
  schema,            // single JSON-LD object or array
  noindex = false,
}) => {
  const canonical = `${BASE_URL}${path}`;
  const fullImage = image.startsWith("http") ? image : `${BASE_URL}${image}`;
  const schemas = Array.isArray(schema) ? schema : schema ? [schema] : [];
  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content="PrestiqueResume" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
};

// ---- Reusable schema fragments ----
export const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "PrestiqueResume",
  url: BASE_URL,
  logo: `${BASE_URL}/brand/logo.png`,
  sameAs: [],
  description: "AI-powered, ATS-friendly CV and resume builder with 200 recruiter-approved templates.",
};

export const SOFTWARE_APP_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "PrestiqueResume",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description: "Build an ATS-friendly CV in 3 minutes with AI rewriting and 20 fully customisable foundations.",
  offers: {
    "@type": "AggregateOffer",
    lowPrice: "0",
    highPrice: "24.99",
    priceCurrency: "GBP",
    offerCount: 4,
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    ratingCount: "127",
  },
};

export const buildFaqSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((it) => ({
    "@type": "Question",
    name: it.q,
    acceptedAnswer: { "@type": "Answer", text: it.a },
  })),
});

/**
 * Build Product schema with Offer entries — used for rich pricing snippets in Google search.
 * Pass the live tiers (from /api/billing/packages plus the Reader free tier).
 */
export const buildPricingProductSchema = (tiers) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: "PrestiqueResume",
  description: "Recruiter-ready resumes and CVs — enhanced by AI, never fabricated. 20 editorial foundations, fully customisable. Global hiring compatibility.",
  brand: { "@type": "Brand", name: "PrestiqueResume" },
  image: `${BASE_URL}/brand/logo.png`,
  url: `${BASE_URL}/pricing`,
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    reviewCount: "1200",
    bestRating: "5",
    worstRating: "1",
  },
  offers: tiers.map((t) => ({
    "@type": "Offer",
    name: t.name,
    price: Number(t.price || 0).toFixed(2),
    priceCurrency: "GBP",
    availability: "https://schema.org/InStock",
    url: `${BASE_URL}/pricing`,
    description: t.blurb || "",
    ...(t.type === "subscription"
      ? {
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: Number(t.price || 0).toFixed(2),
            priceCurrency: "GBP",
            billingDuration: "P1M",
            billingIncrement: 1,
            unitText: "month",
          },
        }
      : {}),
  })),
});

/** Breadcrumb list for inner pages (Home › Page) */
export const buildBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: it.url.startsWith("http") ? it.url : `${BASE_URL}${it.url}`,
  })),
});


export default Seo;
